import { FiDroplet } from 'react-icons/fi';

export const uncountableNounsData = {
  courseId: 'uncountable-nouns',
  courseType: 'vocabulary',
  title: 'Uncountable Nouns',
  icon: FiDroplet,
  overview: {
    impact: 'Avoid common grammatical errors',
    focus: 'Nouns that cannot be counted',
    uses: 'Writing and Speaking',
    duration: '20 minutes',
  },
  sections: [
    {
      title: 'What Are Uncountable Nouns?',
      content: `<p>Uncountable nouns (also called mass nouns) cannot be counted individually. They don't have a plural form and use singular verbs.</p>`,
      examples: [
        { label: 'Correct', text: 'Information is valuable.' },
        { label: 'Wrong', text: 'Informations are valuable.' },
      ],
    },
    {
      title: 'Common Uncountable Nouns',
      content: `<p>These frequently appear in IELTS:</p>`,
      table: {
        headers: ['Category', 'Examples'],
        rows: [
          ['Abstract concepts', 'information, advice, knowledge, research'],
          ['Materials', 'water, air, rice, bread, furniture'],
          ['Academic', 'evidence, data, progress, equipment'],
          ['Feelings', 'happiness, anger, love, fear'],
          ['Activities', 'homework, work, traffic, travel'],
        ],
      },
    },
    {
      title: 'Quantifying Uncountable Nouns',
      content: `<p>Use these expressions to quantify uncountable nouns:</p>`,
      points: [
        '<strong>a piece of:</strong> a piece of advice, a piece of information',
        '<strong>some/any:</strong> some water, any progress',
        '<strong>a lot of / much:</strong> a lot of research, much evidence',
        '<strong>specific units:</strong> a glass of water, a loaf of bread',
      ],
    },
  ],
  exercises: [
    {
      title: 'Correct or Incorrect?',
      type: 'select',
      instructions: 'Is each sentence correct or incorrect?',
      questions: [
        {
          text: '"The researches show positive results."',
          options: ['Incorrect', 'Correct'],
          answer: 'Incorrect',
          explanation: '"Research" is uncountable. Say "The research shows..."',
        },
        {
          text: '"I need some advice about my career."',
          options: ['Correct', 'Incorrect'],
          answer: 'Correct',
          explanation: '"Advice" is uncountable and "some" is appropriate.',
        },
        {
          text: '"We have too many homeworks this week."',
          options: ['Incorrect', 'Correct'],
          answer: 'Incorrect',
          explanation: '"Homework" is uncountable. Say "too much homework".',
        },
        {
          text: '"The equipment are ready for the experiment."',
          options: ['Incorrect', 'Correct'],
          answer: 'Incorrect',
          explanation:
            '"Equipment" is uncountable. Say "The equipment is ready..."',
        },
        {
          text: '"They gave us useful information."',
          options: ['Correct', 'Incorrect'],
          answer: 'Correct',
          explanation: '"Information" is uncountable but used correctly here.',
        },
        {
          text: '"I have many furnitures in my room."',
          options: ['Incorrect', 'Correct'],
          answer: 'Incorrect',
          explanation:
            '"Furniture" is uncountable. Say "much furniture" or "many pieces of furniture".',
        },
      ],
    },
    {
      title: 'Choose the Correct Quantifier',
      type: 'select',
      instructions: 'Select the correct quantifier for each uncountable noun.',
      questions: [
        {
          text: 'Can you give me ___ advice?',
          options: ['some', 'a', 'many'],
          answer: 'some',
          explanation: 'Use "some" with uncountable nouns, not "a" or "many".',
        },
        {
          text: "There isn't ___ evidence to support this claim.",
          options: ['much', 'many', 'a'],
          answer: 'much',
          explanation:
            'Use "much" with uncountable nouns in negative sentences.',
        },
        {
          text: 'I need ___ information about the course.',
          options: ['some', 'an', 'many'],
          answer: 'some',
          explanation: '"Information" is uncountable, so use "some".',
        },
        {
          text: 'She has ___ knowledge of the subject.',
          options: ['a lot of', 'many', 'a'],
          answer: 'a lot of',
          explanation:
            '"A lot of" works with both countable and uncountable nouns.',
        },
      ],
    },
    {
      title: 'Countable or Uncountable?',
      type: 'select',
      instructions: 'Is this noun countable or uncountable?',
      questions: [
        {
          text: 'progress',
          options: ['Uncountable', 'Countable'],
          answer: 'Uncountable',
          explanation:
            '"Progress" is uncountable. Say "make progress" not "make a progress".',
        },
        {
          text: 'suggestion',
          options: ['Countable', 'Uncountable'],
          answer: 'Countable',
          explanation:
            '"Suggestion" is countable. You can say "a suggestion" or "suggestions".',
        },
        {
          text: 'traffic',
          options: ['Uncountable', 'Countable'],
          answer: 'Uncountable',
          explanation:
            '"Traffic" is uncountable. Say "heavy traffic" not "many traffics".',
        },
        {
          text: 'luggage',
          options: ['Uncountable', 'Countable'],
          answer: 'Uncountable',
          explanation:
            '"Luggage" is uncountable. Say "a piece of luggage" for singular.',
        },
      ],
    },
    {
      title: 'Complete with Correct Form',
      type: 'fill-in',
      instructions:
        'Type the correct form of the verb (is/are, was/were, has/have).',
      questions: [
        {
          text: 'The news ___ very concerning.',
          answer: 'is',
          explanation: '"News" is uncountable and takes a singular verb.',
        },
        {
          text: 'The advice she gave me ___ helpful.',
          answer: 'was',
          explanation: '"Advice" is uncountable and takes a singular verb.',
        },
        {
          text: 'The research ___ been completed.',
          answer: 'has',
          explanation: '"Research" is uncountable and takes "has" not "have".',
        },
      ],
    },
    {
      title: 'Rewrite Correctly',
      type: 'writing',
      instructions: 'Rewrite these incorrect sentences correctly.',
      questions: [
        {
          text: '"I received many informations about the program."',
          example: 'I received a lot of information about the program.',
          tip: 'Use "a lot of" or "much" with uncountable nouns.',
        },
        {
          text: '"She gave me two advices."',
          example: 'She gave me two pieces of advice.',
          tip: 'Use "pieces of" to quantify uncountable nouns.',
        },
        {
          text: '"The furnitures in this room are expensive."',
          example: 'The furniture in this room is expensive.',
          tip: '"Furniture" is uncountable—remove the -s and use singular verb.',
        },
      ],
    },
  ],
  tips: [
    'Memorize common uncountable nouns in IELTS topics',
    'Use "some" not "a" with uncountable nouns',
    "Don't add -s to uncountable nouns",
    'Use singular verbs with uncountable nouns',
  ],
  dailyPractice: [
    'Identify uncountable nouns in reading passages',
    'Practice using quantifiers correctly',
    'Check your writing for uncountable noun errors',
    'Learn new uncountable nouns in context',
  ],
  backPath: '/exams/english/#vocabulary',
  backLabel: 'Back to Vocabulary',
  aiContext:
    'Uncountable Nouns: Understanding nouns that cannot be counted, common examples, and how to quantify them correctly.',
};

// Difficulty-variant factory: returns content adapted to the given difficulty level.
// difficulty 1-2: simplified content with basic examples and easier exercises
// difficulty 3: base content (default)
// difficulty 4-5: advanced content with complex examples and harder exercises
export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...uncountableNounsData,
      title: uncountableNounsData.title + ' (Foundations)',
      overview: {
        ...uncountableNounsData.overview,
        duration: '15-20 minutes',
      },
      // Simplified: show only first 2 sections and first exercise set
      sections: uncountableNounsData.sections.slice(0, Math.max(2, Math.ceil(uncountableNounsData.sections.length / 2))),
      exercises: uncountableNounsData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...uncountableNounsData,
      title: uncountableNounsData.title + ' (Advanced)',
      overview: {
        ...uncountableNounsData.overview,
        duration: '30-35 minutes',
      },
      // Advanced: all sections plus advanced tips context
      sections: uncountableNounsData.sections,
      exercises: uncountableNounsData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(uncountableNounsData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS writing tasks.',
      ],
    };
  }
  return uncountableNounsData;
}
